import { zodResolver } from '@hookform/resolvers/zod';
import { Add } from 'iconsax-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ProgressButton from '@/components/common/progress-button';
import { UploadImageField } from '@/components/fields/upload-image-field';
import useReportReasons from '@/hooks/settings/use-report-reasons';
import useSubmitReport, {
  type SubmitReportRequest,
  submitReportRequestScheme,
} from '@/hooks/settings/use-submit-report';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { FieldMessage } from '../fields/field-message';

type Props = {
  sessionId: number;
  type: ConversationType;
  reasonId: number;
  onDone(): void;
};

export default function ConversationReportForm({ type, sessionId, reasonId, onDone }: Props) {
  const { t } = useTranslation();
  const { reasons } = useReportReasons(type);
  const { mutateAsync: submitReport, isPending } = useSubmitReport({
    type,
    sessionId,
    reasonId,
  });

  const {
    register,
    reset,
    watch,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<SubmitReportRequest>({
    resolver: zodResolver(submitReportRequestScheme),
    defaultValues: { Describe: '' },
  });

  const reason = reasons.find((reason) => reason.ID === reasonId)?.Reason ?? '';
  const describeTextCount = watch('Describe')?.length ?? 0;

  const handleSubmitReport = async (report: SubmitReportRequest) => {
    const result = await submitReport(report);

    reset();
    onDone();

    return result;
  };

  return (
    <form
      className='flex w-full flex-1 flex-col gap-4 overflow-y-auto px-4 pb-safe-or-16 desktop:pb-0'
      onSubmit={handleSubmit(handleSubmitReport)}>
      <div>
        <div className='pb-2 font-bold'>{t('conversation.report.reason-title')}</div>
        <input
          value={reason}
          className='input input-sm input-ghost w-full border-none !bg-transparent p-0 text-secondary'
          disabled
        />
      </div>

      <div className='relative'>
        <div className='pb-2 font-bold'>
          {t('conversation.report.describe')}
          <span className='text-red-500'>*</span>
        </div>

        <textarea
          {...register('Describe')}
          maxLength={200}
          minLength={5}
          className='textarea textarea-bordered h-32 w-full resize-none bg-transparent focus:border-primary focus:outline-none'
          placeholder={t('conversation.report.describe-placeholder')}
        />

        <div className='flex items-center justify-end'>
          <FieldMessage
            name='Describe'
            errors={errors}
          />
          <div className='flex-1 text-right text-xs text-secondary'>
            {describeTextCount}
            /200
          </div>
        </div>
      </div>

      <div>
        <div className='pb-2 font-bold'>{t('conversation.report.screenshot')}</div>
        <UploadImageField
          name='Image'
          control={control}
          fallback={
            <div className='flex size-full items-center justify-center border-2 border-dashed text-secondary'>
              <Add className='size-10' />
            </div>
          }
          className='size-32 rounded-lg bg-transparent'>
          {(fieldValue, preview) => (
            <div className='avatar aspect-square size-32'>
              <img
                src={preview || fieldValue?.url || ''}
                alt='group avatar'
                width={100}
                height={100}
              />
            </div>
          )}
        </UploadImageField>
      </div>

      <div className='fixed desktop:static inset-x-4 bottom-4 flex justify-end '>
        <ProgressButton
          as='submit'
          className='btn-primary w-full'
          isLoading={isPending}
          disabled={!isValid}
          label='common.submit'
        />
      </div>
    </form>
  );
}
